import React, { useState, useRef, useCallback, useEffect } from "react";
import styles from "./style.module.scss";
import { ButtonPrimary, CopyElement } from "@components";
import { ReactComponent as SampleIcon } from "../HumanizerInput/assets/svg/Sample.svg";
import { ReactComponent as CopyIcon } from "../HumanizerInput/assets/svg/Copy.svg";
import { Action } from "../HumanizerInput/components";
import { useTranslation } from "react-i18next";
import api from '../../http/axios';
import { Dropdown } from '../Dropdown';

const TONE_OPTIONS = [
    { value: 'professional', label: 'Professional' },
    { value: 'casual', label: 'Casual' },
    { value: 'formal', label: 'Formal' },
    { value: 'diplomatic', label: 'Diplomatic' },
    { value: 'business', label: 'Business' },
];

const SAMPLE_TEXT = "This is a sample text for the Text Message Generator. Provide a topic or keywords, and the AI will generate a message for you. For example, you could enter 'congratulating a friend' or 'business follow-up'.";
const MAX_WORDS = 2000;
const MAX_CHARACTERS = 12000; // Backend limit - roughly 2000 words

const countWords = (str: string): number => {
    str = str.trim();
    if (!str) return 0;
    return str.split(/\s+/).length;
};

export const TextMessageGeneratorInput: React.FC<React.HTMLAttributes<HTMLDivElement>> = (props) => {
    const { t } = useTranslation();
    
    // --- Constants & Refs for resize functionality ---
    const minHeight = 300;
    const maxHeight = 700;
    const defHeight = 350;
    
    const inputTextareaRef = useRef<HTMLTextAreaElement>(null);
    const outputTextareaRef = useRef<HTMLTextAreaElement>(null);
    const resizeHandleRef = useRef<HTMLDivElement>(null);
    const dragInfoRef = useRef<{ startY: number; startHeight: number } | null>(null);
    
    // --- State Variables ---
    const [inputValue, setInputValue] = useState('');
    const [generatedText, setGeneratedText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [textareaHeight, setTextareaHeight] = useState(minHeight);
    const [isDragging, setIsDragging] = useState(false);
    const [manuallyResized, setManuallyResized] = useState(false);
    const [tone, setTone] = useState<string | number>('professional');

    // --- RESIZE HANDLE LOGIC ---
    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            dragInfoRef.current = { startY: e.clientY, startHeight: textareaHeight };
            setIsDragging(true);
            setManuallyResized(true);
        },
        [textareaHeight],
    );

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !dragInfoRef.current) return;
            const deltaY = e.clientY - dragInfoRef.current.startY;
            const newHeight = dragInfoRef.current.startHeight + deltaY;
            const clampedHeight = Math.min(Math.max(newHeight, minHeight), maxHeight);
            setTextareaHeight(clampedHeight);
            if (inputTextareaRef.current) inputTextareaRef.current.style.height = `${clampedHeight}px`;
            if (outputTextareaRef.current) outputTextareaRef.current.style.height = `${clampedHeight}px`;
        },
        [isDragging, minHeight, maxHeight],
    );

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            dragInfoRef.current = null;
        }
    }, [isDragging]);

    useEffect(() => {
        // Global listeners for drag
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "ns-resize";
            document.body.style.userSelect = "none";
        } else {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        }
        return () => {
            // Cleanup
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const handleGenerate = async () => {
        if (!inputValue.trim()) {
            setError(t('textMessageGenerator.errors.enterTopic'));
            return;
        }
        
        const wordCount = countWords(inputValue);
        const charCount = inputValue.length;
        
        if (wordCount > MAX_WORDS) {
            setError(t('textMessageGenerator.errors.tooLongWords', { max: MAX_WORDS }));
            return;
        }
        
        if (charCount > MAX_CHARACTERS) {
            setError(t('textMessageGenerator.errors.tooLongChars', { max: MAX_CHARACTERS }));
            return;
        }
        
        setIsLoading(true);
        setError('');
        setGeneratedText('');
        try {
            const response = await api.post('/free-tools/generate-text-message', { 
                text: inputValue,
                tone: tone
            });
            setGeneratedText(response.data.message);
        } catch (e: any) {
            if (e.response?.data?.message) {
                // Handle validation errors from backend
                const errorMessage = Array.isArray(e.response.data.message) 
                    ? e.response.data.message.join(', ')
                    : e.response.data.message;
                setError(errorMessage);
            } else {
                setError(t('textMessageGenerator.errors.generic'));
            }
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = () => {
        if (generatedText) {
            navigator.clipboard.writeText(generatedText);
        }
    };

    const textAreaChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setInputValue(value);
        setError('');
        
        // Auto-resize logic
        if (inputTextareaRef.current && !manuallyResized) {
            inputTextareaRef.current.style.height = "auto";
            const scrollHeight = inputTextareaRef.current.scrollHeight;
            const newHeight = Math.min(Math.max(scrollHeight, minHeight), defHeight);
            inputTextareaRef.current.style.height = `${newHeight}px`;
            setTextareaHeight(newHeight);
            if (outputTextareaRef.current) outputTextareaRef.current.style.height = `${newHeight}px`;
        } else if (outputTextareaRef.current && inputTextareaRef.current) {
            outputTextareaRef.current.style.height = inputTextareaRef.current.style.height;
        }
        if (manuallyResized) {
            setManuallyResized(false);
        }
    };

    const pasteActionClickHandler = async () => {
        try {
            const text = await navigator.clipboard.readText();
            setInputValue(text);
            if (inputTextareaRef.current) {
                inputTextareaRef.current.focus();
                setManuallyResized(false);
                requestAnimationFrame(() => {
                    if (inputTextareaRef.current) {
                        textAreaChangeHandler({
                            target: inputTextareaRef.current,
                        } as React.ChangeEvent<HTMLTextAreaElement>);
                    }
                });
            }
        } catch (err) {
            console.error('Failed to read clipboard contents: ', err);
        }
    };

    const tryASampleClickHandler = () => {
        setInputValue(SAMPLE_TEXT);
        if (inputTextareaRef.current) {
            inputTextareaRef.current.focus();
            setManuallyResized(false);
            requestAnimationFrame(() => {
                if (inputTextareaRef.current) {
                    textAreaChangeHandler({
                        target: inputTextareaRef.current,
                    } as React.ChangeEvent<HTMLTextAreaElement>);
                }
            });
        }
    };

    const renderActions = () => {
        if (inputValue.trim().length > 0) return null;
        
        return (
            <div className={styles.ActionWrapper}>
                <div className={styles.ActionWrapper__container}>
                    <Action
                        text={t('textMessageGenerator.actions.trySample')}
                        icon={<SampleIcon />}
                        clickHandler={tryASampleClickHandler}
                    />
                    <Action
                        text={t('textMessageGenerator.actions.pasteText')}
                        icon={<CopyIcon />}
                        clickHandler={pasteActionClickHandler}
                    />
                </div>
            </div>
        );
    };

    return (
        <div {...props} className={`${styles.TextMessageGeneratorInput} ${props.className || ""}`}>
            <div className={styles.TextMessageGeneratorInput__flexWrapper}>
                {/* Input Section */}
                <div className={styles.TextMessageGeneratorInput__input}>
                    <div className={styles.TextMessageGeneratorInput__topBar}>
                        <h4 className={styles.TextMessageGeneratorInput__title}>
                            {t('textMessageGenerator.yourText')}
                        </h4>
                        <p className={styles.TextMessageGeneratorInput__wordCountInfo}>
                            <span>{countWords(inputValue)}</span> / {MAX_WORDS} words
                        </p>
                    </div>

                    <div className={styles.TextMessageGeneratorInput__dropdowns}>
                        <div className={styles.dropdownWrapper}>
                            <Dropdown
                                label={t('textMessageGenerator.dropdown.toneOfVoice')}
                                options={TONE_OPTIONS}
                                defaultValue={tone}
                                onSelect={setTone}
                            />
                        </div>
                    </div>
                    
                    <div className={styles.TextMessageGeneratorInput__fieldWrapper}>
                        <textarea
                            ref={inputTextareaRef}
                            className={styles.TextMessageGeneratorInput__field}
                            value={inputValue}
                            onChange={textAreaChangeHandler}
                            placeholder={t('textMessageGenerator.inputTextPlaceholder')}
                            style={{
								height: `${textareaHeight}px`,
								minHeight: `${minHeight}px`,
								maxHeight: `${maxHeight}px`,
							}}
                        />
                        {renderActions()}
                    </div>
                    
                    <div className={styles.buttons}>
                        <div className={styles.leftContent}>
                            {/* Empty for now */}
                        </div>
                        <div className={styles.rightContent}>
                            <ButtonPrimary
                                colorScheme="orange"
                                onClick={handleGenerate}
                                disabled={isLoading}
                            >
                                {isLoading ? t('textMessageGenerator.buttons.processing') : t('textMessageGenerator.buttons.generate')}
                            </ButtonPrimary>
                        </div>
                    </div>
                </div>

                {/* Result Section */}
                <div className={styles.TextMessageGeneratorInput__result}>
                    <div className={styles.TextMessageGeneratorInput__topBar}>
                        <h4 className={styles.TextMessageGeneratorInput__title}>
                            {t('textMessageGenerator.generatedText')}
                        </h4>
                        <CopyElement 
                             className={`${!generatedText || generatedText.length === 0 ? styles.copyDisabled : ""}`}
                             onClick={handleCopy}
                        />
                    </div>

                    <div className={styles.TextMessageGeneratorInput__fieldWrapper}>
                        <textarea
                            ref={outputTextareaRef}
                            className={styles.TextMessageGeneratorInput__result_text}
                            value={generatedText}
                            readOnly
                            placeholder={isLoading ? t('humanizer.processing') : t('textMessageGenerator.outputTextPlaceholder')}
                            style={{
								height: `${textareaHeight}px`,
								minHeight: `${minHeight}px`,
								maxHeight: `${maxHeight}px`,
							}}
                        />
                    </div>
                     <div className={styles.buttons}>
                        <div className={styles.leftContent}>
                            {/* Empty for now */}
                        </div>
                        <div className={styles.rightContent}>
                           {/* The copy button was moved to the top bar */}
                        </div>
                    </div>
                </div>
            </div>
            
            {/* RESIZE SLIDER */}
            <div ref={resizeHandleRef} className={styles.resizeHandle} onMouseDown={handleMouseDown} />

            {error && <p className={styles.errorMessage}>{error}</p>}
        </div>
    );
}; 